import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { api } from '@/lib/api';
import { checkPinStrength } from '@/lib/pin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, UserPlus, Ban, CheckCircle2, LogOut, Clock, Pencil, Calendar, ChevronLeft, ChevronRight, ExternalLink, X, Lock } from 'lucide-react';
import { toast } from 'sonner';

// Drivers are NOT in-store staff — they live under /drivers (Driver Registry).
// Staff Management only manages people who work inside the restaurant.
const BASE_ROLE_OPTIONS = ['kitchen', 'cashier', 'waiter', 'admin'];
// Only an owner can mint another owner — admins must not be able to
// self-elevate or promote others. Backend enforces the same rule.
const OWNER_ROLE_OPTION = 'owner';
// Used by AttendancePanel / ShiftsPanel filter dropdowns (full list, since
// those panels show every role that *could* appear in attendance/shifts —
// even if currently we only schedule floor staff).
const ROLE_OPTIONS = [...BASE_ROLE_OPTIONS, OWNER_ROLE_OPTION];

export default function StaffPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isOwner = user?.role === 'owner';
  // Role list shown in Add/Edit dropdowns depends on who's signed in.
  // Admins cannot see 'owner' so they can't self-elevate or promote.
  const ROLE_OPTIONS_FOR_FORM = isOwner
    ? [...BASE_ROLE_OPTIONS, OWNER_ROLE_OPTION]
    : BASE_ROLE_OPTIONS;
  const [staff, setStaff] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  // Branch list — owners can place staff in any branch; admins are locked
  // to their own branch (the form pre-selects their branch and disables the
  // dropdown). Branch is required for every non-owner role.
  const [branches, setBranches] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await api.get('/branches'); if (alive) setBranches(data || []); }
      catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);
  const adminLockedBranchId = !isOwner ? (user?.branch_id || '') : '';

  const [form, setForm] = useState({ full_name: '', email: '', password: '', role: 'kitchen', phone: '', branch_id: '' });
  // Whenever the admin opens the dialog, pre-select their own branch (and
  // remember it for subsequent opens). For owners, the form remembers the
  // last selected branch so they can rapidly add multiple staff to it.
  React.useEffect(() => {
    if (!isOwner && adminLockedBranchId && !form.branch_id) {
      setForm((f) => ({ ...f, branch_id: adminLockedBranchId }));
    } else if (isOwner && !form.branch_id && branches.length) {
      setForm((f) => ({ ...f, branch_id: user?.branch_id || branches[0].id }));
    }
  }, [adminLockedBranchId, isOwner, branches, user, form.branch_id]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', role: 'kitchen', phone: '', kiosk_pin: '', branch_id: '' });

  React.useEffect(() => {
    if (editing) setEditForm({ full_name: editing.full_name || '', email: editing.email || '', role: editing.role, phone: editing.phone || '', kiosk_pin: '', branch_id: editing.branch_id || '' });
  }, [editing]);

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.kiosk_pin) delete payload.kiosk_pin;
      else {
        const reason = checkPinStrength(payload.kiosk_pin);
        if (reason) { toast.error(reason); return; }
      }
      await api.patch(`/staff/${editing.id}`, payload);
      toast.success('Staff updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const load = useCallback(async () => {
    try {
      const s = await api.get('/staff');
      setStaff(s.data || []);
    } catch (e) { toast.error('Failed to load staff'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      // Validate locally before bothering the server (better UX).
      if (form.role !== 'owner' && !form.branch_id) {
        toast.error('Pick a branch for this staff member.'); return;
      }
      await api.post('/auth/staff', form);
      toast.success('Staff created');
      setShowAdd(false);
      setForm({ full_name: '', email: '', password: '', role: 'kitchen', phone: '', branch_id: adminLockedBranchId || form.branch_id });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (u) => {
    try {
      if (u.disabled) await api.patch(`/staff/${u.id}/enable`);
      else await api.patch(`/staff/${u.id}/disable`);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Staff management</h1>
          <p className="text-xs text-muted-foreground">{user?.full_name} · {user?.role}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => nav('/server')}>Server</Button>
          <Button size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger data-testid="staff-tab-members" value="members">Members</TabsTrigger>
            <TabsTrigger data-testid="staff-tab-attendance" value="attendance">Attendance</TabsTrigger>
            <TabsTrigger data-testid="staff-tab-shifts" value="shifts">Shifts</TabsTrigger>
          </TabsList>
          <TabsContent value="members">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Members ({staff.length})</CardTitle>
                <div className="flex items-center gap-2">
                  <Button data-testid="staff-open-kiosk-button" size="sm" variant="secondary" onClick={() => window.open('/checkin', '_blank')}><ExternalLink className="mr-1 h-4 w-4" /> Open Kiosk</Button>
                  <Dialog open={showAdd} onOpenChange={setShowAdd}>
                  <DialogTrigger asChild><Button data-testid="staff-add-button" size="sm"><UserPlus className="mr-1 h-4 w-4" /> Add staff</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>New staff member</DialogTitle><DialogDescription>Create an account and assign a role.</DialogDescription></DialogHeader>
                    <div className="grid gap-3">
                      <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="staff-add-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>Email</Label><Input data-testid="staff-add-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>Password (8+ chars)</Label><Input data-testid="staff-add-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Role</Label>
                          <select data-testid="staff-add-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm capitalize">
                            {ROLE_OPTIONS_FOR_FORM.map((r) => (<option key={r} value={r} className="capitalize">{r}</option>))}
                          </select>
                        </div>
                      </div>
                      {form.role !== 'owner' ? (
                        <div className="space-y-1.5">
                          <Label>Branch <span className="text-rose-600">*</span></Label>
                          <select
                            data-testid="staff-add-branch-select"
                            value={form.branch_id}
                            onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                            disabled={!isOwner}
                            className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <option value="" disabled>Select a branch&hellip;</option>
                            {branches.map((b) => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                          {!isOwner && (
                            <p className="text-[11px] text-muted-foreground">
                              Admins can only add staff to their own branch. Ask an owner to add staff to a different branch.
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="rounded-md border border-dashed border-border bg-muted/40 p-2 text-[11px] text-muted-foreground">
                          Owners are not tied to a branch &mdash; they have access across the whole chain.
                        </p>
                      )}
                    </div>
                    <DialogFooter><Button data-testid="staff-add-submit-button" onClick={create} disabled={!form.full_name || !form.email || form.password.length < 8 || (form.role !== 'owner' && !form.branch_id)}>Create</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
                </div>
              </CardHeader>
              <CardContent className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Status</th><th className="p-2">Kiosk PIN</th><th className="p-2"></th></tr></thead>
                  <tbody>
                    {staff.map((u) => (
                      <tr key={u.id} data-testid="staff-member-row" className="border-t border-border">
                        <td className="p-2 font-medium">{u.full_name}</td>
                        <td className="p-2 text-muted-foreground">{u.email}</td>
                        <td className="p-2 capitalize"><Badge variant="secondary">{u.role}</Badge></td>
                        <td className="p-2">{u.disabled ? <Badge className="border border-rose-200 bg-rose-50 text-rose-900">Disabled</Badge> : <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-900">Active</Badge>}</td>
                        <td className="p-2">
                          {['admin', 'owner'].includes(u.role) ? (
                            <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="staff-pin-na-badge">
                              Manager &mdash; no kiosk
                            </Badge>
                          ) : (
                            <>
                              {u.has_kiosk_pin ? <Badge data-testid="staff-pin-set-badge" className="border border-emerald-200 bg-emerald-50 text-emerald-900"><CheckCircle2 className="mr-1 h-3 w-3" /> Set</Badge> : <Badge data-testid="staff-pin-missing-badge" className="border border-amber-200 bg-amber-50 text-amber-900">Not set</Badge>}
                              {u.kiosk_pin_updated_at && (
                                <span className="ml-2 text-[10px] text-muted-foreground">
                                  {(() => { try { return new Date(u.kiosk_pin_updated_at).toLocaleDateString(); } catch (e) { return ''; } })()}
                                </span>
                              )}
                              {u.has_kiosk_pin && (
                                <Button
                                  data-testid="staff-pin-reset-button"
                                  size="sm"
                                  variant="ghost"
                                  className="ml-1 h-6 px-1.5 text-[11px] text-amber-700 hover:text-amber-900"
                                  onClick={async () => {
                                    if (!window.confirm(`Reset ${u.full_name}'s kiosk PIN? They'll be unable to clock in until they set a new one.`)) return;
                                    try { await api.post(`/staff/${u.id}/pin/reset`); toast.success('PIN reset'); load(); }
                                    catch (e) { toast.error('Failed'); }
                                  }}
                                >Reset</Button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="p-2">
                          {/* Rule 1: Admins cannot modify owner accounts at
                              all. We hide the edit/disable buttons and
                              show a small explanatory chip instead so the
                              admin understands why they can't act. */}
                          {(['owner'].includes(u.role) && !isOwner) ? (
                            <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="staff-owner-locked-badge">
                              <Lock className="mr-1 h-3 w-3" /> Owner &mdash; locked
                            </Badge>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Button data-testid="staff-edit-button" size="sm" variant="secondary" onClick={() => setEditing(u)}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                              <Button data-testid="staff-toggle-button" size="sm" variant="secondary" onClick={() => toggle(u)} disabled={u.id === user.id}>
                                {u.disabled ? <><CheckCircle2 className="mr-1 h-4 w-4" /> Enable</> : <><Ban className="mr-1 h-4 w-4" /> Disable</>}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="attendance">
            {/* Attendance is tracked for hourly floor staff only — managers
                (admin/owner) and drivers do not clock in at the kiosk and
                are excluded from this list. */}
            <AttendancePanel staff={staff.filter((s) => ['kitchen', 'cashier', 'waiter'].includes(s.role))} />
          </TabsContent>
          <TabsContent value="shifts">
            {/* Same: only floor staff are scheduled. */}
            <ShiftsPanel staff={staff.filter((s) => ['kitchen', 'cashier', 'waiter'].includes(s.role))} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit staff member</DialogTitle><DialogDescription>Update profile and role.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="staff-edit-name-input" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="staff-edit-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Role</Label>
              <select data-testid="staff-edit-role-select" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm capitalize">
                {ROLE_OPTIONS_FOR_FORM.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
              {!isOwner && editing?.role === 'owner' && (
                <p className="text-[11px] text-amber-700">Only an owner can change another owner&apos;s role.</p>
              )}
            </div>
            {/* Branch — owners can transfer staff between branches; admins
                see the field read-only as context but cannot change it. */}
            {editForm.role !== 'owner' && (
              <div className="space-y-1.5">
                <Label>Branch {isOwner ? <span className="text-rose-600">*</span> : null}</Label>
                <select
                  data-testid="staff-edit-branch-select"
                  value={editForm.branch_id || ''}
                  onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })}
                  disabled={!isOwner}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="" disabled>Select a branch&hellip;</option>
                  {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
                {!isOwner && (
                  <p className="text-[11px] text-muted-foreground">
                    Only an owner can transfer a staff member to a different branch.
                  </p>
                )}
                {isOwner && editForm.branch_id !== (editing?.branch_id || '') && (
                  <p className="text-[11px] text-amber-700">
                    You&apos;re moving this staff member to a different branch.
                  </p>
                )}
              </div>
            )}
            {/* PIN editor is irrelevant for managers — admin/owner don't use the entrance kiosk. */}
            {!['admin', 'owner'].includes(editForm.role) && (
            <div className="space-y-1.5">
              <Label>Kiosk PIN <span className="text-xs font-normal text-muted-foreground">(exactly 5 digits, scattered — no 12345 or 11111)</span></Label>
              <Input
                data-testid="staff-edit-pin-input"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={5}
                value={editForm.kiosk_pin}
                onChange={(e) => setEditForm({ ...editForm, kiosk_pin: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                placeholder={editing?.has_kiosk_pin ? 'Leave blank to keep current PIN' : 'e.g. 73914'}
              />
              {editForm.kiosk_pin && (() => {
                const reason = checkPinStrength(editForm.kiosk_pin);
                return reason ? (
                  <p data-testid="staff-edit-pin-error" className="text-xs text-rose-600">{reason}</p>
                ) : (
                  <p className="text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" /> Strong PIN.</p>
                );
              })()}
              {editing?.has_kiosk_pin && !editForm.kiosk_pin && (
                <p className="text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" /> This member already has a PIN. Type a new one to replace it.</p>
              )}
            </div>
            )}
          </div>
          <DialogFooter><Button data-testid="staff-edit-save-button" onClick={saveEdit}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Shift templates (per user spec) ----------
const SHIFT_TEMPLATES = [
  { key: 'regular', label: 'Regular', short: 'R',   hint: '09:00 – 17:00', start: '09:00', end: '17:00', status: 'scheduled', cell: 'bg-emerald-100 text-emerald-900 border-emerald-200', dot: 'bg-emerald-500' },
  { key: 'morning', label: 'Morning', short: 'M',   hint: '06:00 – 14:00', start: '06:00', end: '14:00', status: 'scheduled', cell: 'bg-sky-100 text-sky-900 border-sky-200',                 dot: 'bg-sky-500' },
  { key: 'evening', label: 'Evening', short: 'E',   hint: '14:00 – 22:00', start: '14:00', end: '22:00', status: 'scheduled', cell: 'bg-indigo-100 text-indigo-900 border-indigo-200',       dot: 'bg-indigo-500' },
  { key: 'night',   label: 'Night',   short: 'N',   hint: '22:00 – 06:00', start: '22:00', end: '06:00', status: 'scheduled', cell: 'bg-slate-200 text-slate-900 border-slate-300',          dot: 'bg-slate-700' },
  { key: 'pto',     label: 'PTO',     short: 'PTO', hint: 'Time off',      start: '00:00', end: '00:00', status: 'pto',       cell: 'bg-violet-100 text-violet-900 border-violet-200',       dot: 'bg-violet-500' },
  { key: 'sick',    label: 'Sick',    short: 'S',   hint: 'Sick day',      start: '00:00', end: '00:00', status: 'sick',      cell: 'bg-amber-100 text-amber-900 border-amber-200',          dot: 'bg-amber-500' },
];

function tplFor(shift) {
  if (!shift) return null;
  if (shift.status === 'pto') return SHIFT_TEMPLATES.find((t) => t.key === 'pto');
  if (shift.status === 'sick') return SHIFT_TEMPLATES.find((t) => t.key === 'sick');
  const match = SHIFT_TEMPLATES.find((t) => t.start === shift.start_time && t.end === shift.end_time);
  return match || { key: 'custom', label: 'Custom', short: 'C', hint: `${shift.start_time} – ${shift.end_time}`, cell: 'bg-stone-100 text-stone-900 border-stone-200', dot: 'bg-stone-500' };
}

function ymd(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
}
function startOfWeek(d) {
  const t = new Date(d);
  const dow = t.getDay(); // Sun=0
  t.setDate(t.getDate() - dow);
  t.setHours(0, 0, 0, 0);
  return t;
}
function startOfMonth(d) { const t = new Date(d); t.setDate(1); t.setHours(0, 0, 0, 0); return t; }
function endOfMonth(d) { const t = new Date(d); t.setMonth(t.getMonth() + 1, 0); t.setHours(23, 59, 59, 999); return t; }

function ShiftsPanel({ staff }) {
  const [viewMode, setViewMode] = useState('week'); // 'day' | 'week' | 'month'
  const [anchor, setAnchor] = useState(new Date());  // navigation anchor
  const [shifts, setShifts] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [filterRole, setFilterRole] = useState('all');

  // Compute the date window for current view
  const { from, to, days } = React.useMemo(() => {
    if (viewMode === 'day') {
      const d = new Date(anchor); d.setHours(0, 0, 0, 0);
      return { from: ymd(d), to: ymd(d), days: [new Date(d)] };
    }
    if (viewMode === 'week') {
      const s = startOfWeek(anchor);
      const arr = Array.from({ length: 7 }, (_, i) => { const t = new Date(s); t.setDate(s.getDate() + i); return t; });
      return { from: ymd(arr[0]), to: ymd(arr[6]), days: arr };
    }
    // month
    const s = startOfMonth(anchor);
    const e = endOfMonth(anchor);
    // Calendar grid: previous Sunday to next Saturday so weeks line up
    const gridStart = startOfWeek(s);
    const gridEnd = new Date(e); gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));
    const arr = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) { arr.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return { from: ymd(gridStart), to: ymd(gridEnd), days: arr };
  }, [viewMode, anchor]);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get('/staff/shifts', { params: { date_from: from, date_to: to } });
      setShifts(data || []);
    } catch (e) { toast.error('Failed to load shifts'); }
  }, [from, to]);
  React.useEffect(() => { load(); }, [load]);

  const navStep = (dir) => {
    const t = new Date(anchor);
    if (viewMode === 'day') t.setDate(t.getDate() + dir);
    else if (viewMode === 'week') t.setDate(t.getDate() + 7 * dir);
    else t.setMonth(t.getMonth() + dir);
    setAnchor(t);
  };

  // Index shifts: by (user_id|date) and (date) for month view
  const cellIndex = React.useMemo(() => {
    const m = {};
    for (const s of shifts) {
      const k = `${s.user_id}|${s.date}`;
      (m[k] = m[k] || []).push(s);
    }
    return m;
  }, [shifts]);
  const dateIndex = React.useMemo(() => {
    const m = {};
    for (const s of shifts) { (m[s.date] = m[s.date] || []).push(s); }
    return m;
  }, [shifts]);

  const employees = React.useMemo(() => {
    const list = staff.filter((s) => !s.disabled);
    if (filterRole !== 'all') return list.filter((s) => s.role === filterRole);
    return list;
  }, [staff, filterRole]);

  const assign = async (user_id, date, tplKey) => {
    const tpl = SHIFT_TEMPLATES.find((t) => t.key === tplKey);
    if (!tpl) return;
    setBusyKey(`${user_id}|${date}`);
    try {
      // If a shift already exists for this user+date, update it; else create.
      const existing = cellIndex[`${user_id}|${date}`]?.[0];
      if (existing) {
        await api.patch(`/staff/shifts/${existing.id}`, {
          start_time: tpl.start, end_time: tpl.end, status: tpl.status,
        });
      } else {
        await api.post('/staff/shifts', {
          user_id, date, start_time: tpl.start, end_time: tpl.end, status: tpl.status,
        });
      }
      toast.success(`${tpl.label} set`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyKey(null); }
  };

  const removeAt = async (user_id, date) => {
    const existing = cellIndex[`${user_id}|${date}`]?.[0];
    if (!existing) return;
    setBusyKey(`${user_id}|${date}`);
    try {
      await api.delete(`/staff/shifts/${existing.id}`);
      toast.success('Shift removed');
      await load();
    } catch (e) { toast.error('Failed'); }
    finally { setBusyKey(null); }
  };

  // Header label per view
  const headerLabel = React.useMemo(() => {
    if (viewMode === 'day') return anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    if (viewMode === 'week') {
      const s = days[0]; const e = days[6];
      const sameMonth = s.getMonth() === e.getMonth();
      const left = s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const right = sameMonth
        ? e.toLocaleDateString(undefined, { day: 'numeric', year: 'numeric' })
        : e.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      return `${left} – ${right}`;
    }
    return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }, [viewMode, anchor, days]);

  const todayKey = ymd(new Date());

  return (
    <Card>
      {/* Toolbar */}
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-5 w-5 text-primary" /> Shift schedule
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex overflow-hidden rounded-md border border-border" role="tablist">
            {['day', 'week', 'month'].map((v) => (
              <button
                key={v}
                data-testid={`shifts-view-${v}`}
                onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${viewMode === v ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
              >{v}</button>
            ))}
          </div>
          {/* Role filter */}
          <select
            data-testid="shifts-role-filter"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs capitalize"
          >
            <option value="all">All roles</option>
            {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
          {/* Nav */}
          <Button data-testid="shifts-prev-button" size="sm" variant="secondary" className="h-8" onClick={() => navStep(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button data-testid="shifts-today-button" size="sm" variant="secondary" className="h-8" onClick={() => setAnchor(new Date())}>Today</Button>
          <Button data-testid="shifts-next-button" size="sm" variant="secondary" className="h-8" onClick={() => navStep(1)}><ChevronRight className="h-4 w-4" /></Button>
          <span className="ml-1 min-w-[160px] text-right text-xs font-medium text-muted-foreground tabular">{headerLabel}</span>
        </div>
      </CardHeader>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-6 pb-3 text-[11px] text-muted-foreground">
        <span className="uppercase tracking-wider">Legend:</span>
        {SHIFT_TEMPLATES.map((t) => (
          <span key={t.key} className="flex items-center gap-1">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${t.dot}`} /> {t.label}
            <span className="text-muted-foreground/70">· {t.hint}</span>
          </span>
        ))}
      </div>

      <CardContent className="space-y-3">
        {/* WEEK VIEW (default) */}
        {viewMode === 'week' && (
          <div className="overflow-auto rounded border border-border">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead>
                <tr className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="sticky left-0 z-10 w-44 border-b border-border bg-muted/40 p-2 text-left">Employee</th>
                  {days.map((d) => {
                    const isToday = ymd(d) === todayKey;
                    return (
                      <th key={ymd(d)} className={`border-b border-l border-border p-2 text-center ${isToday ? 'bg-primary/5 text-primary' : ''}`}>
                        <div className="text-[10px]">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                        <div className={`font-display text-base ${isToday ? 'text-primary' : 'text-foreground'}`}>{d.getDate()}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {!employees.length && (
                  <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">No matching staff.</td></tr>
                )}
                {employees.map((emp) => (
                  <tr key={emp.id} data-testid="shifts-employee-row" className="hover:bg-muted/20">
                    <td className="sticky left-0 z-10 border-b border-border bg-background p-2">
                      <p className="text-sm font-medium leading-tight">{emp.full_name}</p>
                      <p className="text-[11px] capitalize text-muted-foreground">{emp.role}</p>
                    </td>
                    {days.map((d) => (
                      <ShiftCell
                        key={`${emp.id}-${ymd(d)}`}
                        emp={emp}
                        date={ymd(d)}
                        shift={cellIndex[`${emp.id}|${ymd(d)}`]?.[0]}
                        busy={busyKey === `${emp.id}|${ymd(d)}`}
                        onAssign={(tpl) => assign(emp.id, ymd(d), tpl)}
                        onRemove={() => removeAt(emp.id, ymd(d))}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <div className="overflow-hidden rounded border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="border-b border-border p-2 text-left">Employee</th>
                  <th className="border-b border-border p-2 text-left">Role</th>
                  <th className="border-b border-border p-2 text-center">Shift</th>
                  <th className="border-b border-border p-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {!employees.length && (
                  <tr><td colSpan={4} className="p-6 text-center text-sm text-muted-foreground">No matching staff.</td></tr>
                )}
                {employees.map((emp) => {
                  const dateKey = ymd(anchor);
                  const sh = cellIndex[`${emp.id}|${dateKey}`]?.[0];
                  return (
                    <tr key={emp.id} data-testid="shifts-day-row" className="border-t border-border">
                      <td className="p-2 font-medium">{emp.full_name}</td>
                      <td className="p-2 capitalize text-muted-foreground">{emp.role}</td>
                      <td className="p-2 text-center">
                        {sh ? (
                          (() => { const t = tplFor(sh); return (
                            <Badge className={`border ${t.cell}`}>{t.label} <span className="ml-1 opacity-70">{sh.start_time}–{sh.end_time}</span></Badge>
                          ); })()
                        ) : <span className="text-xs text-muted-foreground">— Off —</span>}
                      </td>
                      <td className="p-2 text-right">
                        <ShiftCellTrigger
                          emp={emp}
                          date={dateKey}
                          shift={sh}
                          busy={busyKey === `${emp.id}|${dateKey}`}
                          onAssign={(tpl) => assign(emp.id, dateKey, tpl)}
                          onRemove={() => removeAt(emp.id, dateKey)}
                          asButton
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* MONTH VIEW (calendar) */}
        {viewMode === 'month' && (
          <div className="overflow-hidden rounded border border-border">
            <div className="grid grid-cols-7 bg-muted/40 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} className="border-b border-border p-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d) => {
                const k = ymd(d);
                const isCurrentMonth = d.getMonth() === anchor.getMonth();
                const isToday = k === todayKey;
                const dayShifts = (dateIndex[k] || []).filter((s) => employees.some((e) => e.id === s.user_id));
                // Group by template for stacked badges
                const grouped = dayShifts.reduce((acc, s) => {
                  const t = tplFor(s);
                  const key = t?.key || 'custom';
                  (acc[key] = acc[key] || { tpl: t, items: [] }).items.push(s);
                  return acc;
                }, {});
                return (
                  <button
                    key={k}
                    data-testid="shifts-month-cell"
                    onClick={() => { setAnchor(new Date(d)); setViewMode('day'); }}
                    className={`relative min-h-[96px] border-b border-l border-border p-2 text-left transition hover:bg-muted/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${isCurrentMonth ? 'bg-card' : 'bg-muted/10'}`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className={`font-display text-sm ${isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/60'} ${isToday ? '!text-primary font-semibold' : ''}`}>{d.getDate()}</span>
                      {dayShifts.length > 0 && (
                        <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{dayShifts.length}</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {Object.values(grouped).slice(0, 4).map(({ tpl, items }) => (
                        <span key={tpl?.key || 'c'} className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] ${tpl?.cell || 'bg-stone-100 text-stone-900 border-stone-200'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${tpl?.dot || 'bg-stone-500'}`} />{tpl?.short} ·{items.length}
                        </span>
                      ))}
                      {Object.keys(grouped).length > 4 && (
                        <span className="text-[10px] text-muted-foreground">+{Object.keys(grouped).length - 4}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="border-t border-border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
              Tip: tap a day to switch to <strong>Day view</strong> and assign shifts.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** A clickable cell in the week grid with a popover for assigning shift templates. */
function ShiftCell({ emp, date, shift, busy, onAssign, onRemove }) {
  const tpl = tplFor(shift);
  return (
    <td className="border-b border-l border-border p-1 align-top">
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="shifts-cell-button"
            disabled={busy}
            className={`relative flex h-14 w-full items-center justify-center rounded border px-1 text-[11px] font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${shift ? tpl.cell : 'border-dashed border-border bg-background text-muted-foreground hover:border-primary hover:bg-primary/5'}`}
            aria-label={shift ? `${tpl.label} on ${date}` : `Assign shift on ${date}`}
          >
            {shift ? (
              <span className="flex flex-col items-center">
                <span className="font-display text-xs">{tpl.label}</span>
                {tpl.key !== 'pto' && tpl.key !== 'sick' && (
                  <span className="text-[10px] opacity-70">{shift.start_time}–{shift.end_time}</span>
                )}
              </span>
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-60 p-2">
          <ShiftPopoverBody emp={emp} date={date} shift={shift} onAssign={onAssign} onRemove={onRemove} />
        </PopoverContent>
      </Popover>
    </td>
  );
}

/** Trigger used in day view (renders a Button instead of a cell). */
function ShiftCellTrigger({ emp, date, shift, busy, onAssign, onRemove }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid="shifts-day-assign-button" size="sm" variant="secondary" disabled={busy}>
          {shift ? <><Pencil className="mr-1 h-3.5 w-3.5" /> Change</> : <><Plus className="mr-1 h-3.5 w-3.5" /> Assign</>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-2">
        <ShiftPopoverBody emp={emp} date={date} shift={shift} onAssign={onAssign} onRemove={onRemove} />
      </PopoverContent>
    </Popover>
  );
}

function ShiftPopoverBody({ emp, date, shift, onAssign, onRemove }) {
  const friendly = new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return (
    <div className="space-y-2">
      <div className="px-1">
        <p className="text-xs font-semibold leading-tight">{emp.full_name}</p>
        <p className="text-[11px] text-muted-foreground">{friendly} · <span className="capitalize">{emp.role}</span></p>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {SHIFT_TEMPLATES.map((t) => (
          <button
            key={t.key}
            data-testid={`shifts-tpl-${t.key}`}
            onClick={() => onAssign(t.key)}
            className={`flex items-center gap-2 rounded border px-2 py-1.5 text-left text-xs transition hover:border-primary ${t.cell}`}
          >
            <span className={`inline-block h-2 w-2 rounded-full ${t.dot}`} />
            <span className="flex-1">
              <span className="block font-semibold">{t.label}</span>
              <span className="block text-[10px] opacity-70">{t.hint}</span>
            </span>
          </button>
        ))}
      </div>
      {shift && (
        <Button data-testid="shifts-cell-remove" size="sm" variant="ghost" className="w-full text-destructive hover:text-destructive" onClick={onRemove}>
          <X className="mr-1 h-3.5 w-3.5" /> Remove shift
        </Button>
      )}
    </div>
  );
}

// ============================================================
// ATTENDANCE PANEL — Day view + per-staff Month view with shift correlation
// ============================================================
const LATE_THRESHOLD_MIN = 10; // tolerance before "Late" badge appears

const ATT_STATUS = {
  on_time:     { label: 'On time',    cell: 'bg-emerald-100 text-emerald-900 border-emerald-200',  dot: 'bg-emerald-500' },
  late:        { label: 'Late',       cell: 'bg-amber-100 text-amber-900 border-amber-200',         dot: 'bg-amber-500' },
  missed:      { label: 'Missed',     cell: 'bg-rose-100 text-rose-900 border-rose-200',            dot: 'bg-rose-500' },
  unscheduled: { label: 'Unscheduled',cell: 'bg-sky-100 text-sky-900 border-sky-200',               dot: 'bg-sky-500' },
  on_shift:    { label: 'On shift',   cell: 'bg-blue-100 text-blue-900 border-blue-200',            dot: 'bg-blue-500' },
  pto:         { label: 'PTO',        cell: 'bg-violet-100 text-violet-900 border-violet-200',     dot: 'bg-violet-500' },
  sick:        { label: 'Sick',       cell: 'bg-amber-50 text-amber-900 border-amber-200',          dot: 'bg-amber-500' },
  off:         { label: 'Off',        cell: 'bg-stone-100 text-stone-900 border-stone-200',         dot: 'bg-stone-400' },
  scheduled:   { label: 'Scheduled',  cell: 'bg-stone-100 text-stone-900 border-stone-200',         dot: 'bg-stone-500' },
};

function hhmmFromIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function diffMinutesHHMM(hhmmA, hhmmB) {
  if (!hhmmA || !hhmmB) return null;
  const [ah, am] = hhmmA.split(':').map(Number);
  const [bh, bm] = hhmmB.split(':').map(Number);
  return (bh * 60 + bm) - (ah * 60 + am);
}

/** Derive attendance status for a (shift?, attendance?) pair on a given date.
 *  Returns { key, sched, actualIn, actualOut, mins, lateBy }. */
function deriveStatus({ shift, att, dateKey, todayKey }) {
  // PTO / Sick override everything
  if (shift && shift.status === 'pto')  return { key: 'pto',  sched: shift, att, mins: null, lateBy: 0 };
  if (shift && shift.status === 'sick') return { key: 'sick', sched: shift, att, mins: null, lateBy: 0 };

  const actualIn = att ? hhmmFromIso(att.clock_in) : null;
  const actualOut = att && att.clock_out ? hhmmFromIso(att.clock_out) : null;
  const mins = att && att.duration_minutes != null ? att.duration_minutes : null;

  if (att && !att.clock_out) {
    return { key: 'on_shift', sched: shift, att, actualIn, actualOut: null, mins };
  }

  // Scheduled cases
  if (shift && (shift.status === 'scheduled' || shift.status === 'completed')) {
    if (!att) {
      if (dateKey < todayKey) return { key: 'missed', sched: shift, att: null, mins: null };
      return { key: 'scheduled', sched: shift, att: null, mins: null };
    }
    const lateBy = diffMinutesHHMM(shift.start_time, actualIn);
    if (lateBy != null && lateBy > LATE_THRESHOLD_MIN) {
      return { key: 'late', sched: shift, att, actualIn, actualOut, mins, lateBy };
    }
    return { key: 'on_time', sched: shift, att, actualIn, actualOut, mins, lateBy };
  }

  // No shift scheduled
  if (att) return { key: 'unscheduled', sched: null, att, actualIn, actualOut, mins };
  return { key: 'off', sched: null, att: null };
}

function AttendancePanel({ staff }) {
  const [viewMode, setViewMode] = React.useState('day'); // 'day' | 'month'
  const [day, setDay] = React.useState(() => ymd(new Date()));
  const [filterRole, setFilterRole] = React.useState('all');
  const [employeeId, setEmployeeId] = React.useState(''); // for month view
  const [monthAnchor, setMonthAnchor] = React.useState(new Date());
  const [shifts, setShifts] = React.useState([]);
  const [attendance, setAttendance] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Default to first staff member for month view
  React.useEffect(() => {
    if (!employeeId && staff.length) {
      const firstActive = staff.find((s) => !s.disabled) || staff[0];
      setEmployeeId(firstActive?.id || '');
    }
  }, [staff, employeeId]);

  const todayKey = ymd(new Date());

  const employees = React.useMemo(() => {
    const list = staff.filter((s) => !s.disabled);
    if (filterRole !== 'all') return list.filter((s) => s.role === filterRole);
    return list;
  }, [staff, filterRole]);

  // Window for fetching
  const { from, to, monthDays } = React.useMemo(() => {
    if (viewMode === 'day') return { from: day, to: day, monthDays: [] };
    // month: cover full grid (prev sun .. next sat)
    const s = startOfMonth(monthAnchor);
    const e = endOfMonth(monthAnchor);
    const gridStart = startOfWeek(s);
    const gridEnd = new Date(e); gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));
    const arr = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) { arr.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return { from: ymd(gridStart), to: ymd(gridEnd), monthDays: arr };
  }, [viewMode, day, monthAnchor]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const shiftParams = { date_from: from, date_to: to };
      const attParams = { date_from: from, date_to: to };
      if (viewMode === 'month' && employeeId) {
        shiftParams.user_id = employeeId;
        attParams.user_id = employeeId;
      }
      const [sh, at] = await Promise.all([
        api.get('/staff/shifts', { params: shiftParams }),
        api.get('/staff/attendance', { params: attParams }),
      ]);
      setShifts(sh.data || []);
      setAttendance(at.data || []);
    } catch (e) { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [from, to, viewMode, employeeId]);
  React.useEffect(() => { load(); }, [load]);

  // Build helpers
  const shiftByKey = React.useMemo(() => {
    const m = {};
    for (const s of shifts) m[`${s.user_id}|${s.date}`] = s;
    return m;
  }, [shifts]);
  const attByKey = React.useMemo(() => {
    // Keep the earliest clock-in per (user, date); also track open shift
    const m = {};
    for (const a of attendance) {
      const k = `${a.user_id}|${(a.clock_in || '').slice(0, 10)}`;
      if (!m[k]) m[k] = a;
      else {
        // Prefer entries with clock_out null (currently on shift) over completed older ones, otherwise keep earliest
        if (m[k].clock_out && !a.clock_out) m[k] = a;
        else if (m[k].clock_in > a.clock_in) m[k] = a;
      }
    }
    return m;
  }, [attendance]);

  // Day navigation helpers
  const dayShift = (d) => (s) => `${d.toLocaleDateString(undefined, s)}`;
  const navDay = (dir) => {
    const t = new Date(day + 'T00:00:00'); t.setDate(t.getDate() + dir);
    setDay(ymd(t));
  };
  const navMonth = (dir) => {
    const t = new Date(monthAnchor); t.setMonth(t.getMonth() + dir);
    setMonthAnchor(t);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-5 w-5 text-primary" /> Attendance
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex overflow-hidden rounded-md border border-border" role="tablist">
            {['day', 'month'].map((v) => (
              <button
                key={v}
                data-testid={`att-view-${v}`}
                onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${viewMode === v ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
              >{v}</button>
            ))}
          </div>
          {viewMode === 'day' ? (
            <>
              <select
                data-testid="att-role-filter"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs capitalize"
              >
                <option value="all">All roles</option>
                {ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
              <Button data-testid="att-prev-day" size="sm" variant="secondary" className="h-8" onClick={() => navDay(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button data-testid="att-today-day" size="sm" variant="secondary" className="h-8" onClick={() => setDay(ymd(new Date()))}>Today</Button>
              <Input
                data-testid="att-day-picker"
                type="date"
                value={day}
                max={ymd(new Date())}
                onChange={(e) => setDay(e.target.value)}
                className="h-8 w-40 text-xs"
              />
              <Button data-testid="att-next-day" size="sm" variant="secondary" className="h-8" disabled={day >= ymd(new Date())} onClick={() => navDay(1)}><ChevronRight className="h-4 w-4" /></Button>
            </>
          ) : (
            <>
              <select
                data-testid="att-employee-select"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                {staff.map((s) => (<option key={s.id} value={s.id}>{s.full_name} · {s.role}</option>))}
              </select>
              <Button data-testid="att-prev-month" size="sm" variant="secondary" className="h-8" onClick={() => navMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button data-testid="att-today-month" size="sm" variant="secondary" className="h-8" onClick={() => setMonthAnchor(new Date())}>Today</Button>
              <Button data-testid="att-next-month" size="sm" variant="secondary" className="h-8" onClick={() => navMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
              <span className="ml-1 min-w-[120px] text-right text-xs font-medium text-muted-foreground tabular">{monthAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
            </>
          )}
        </div>
      </CardHeader>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-6 pb-3 text-[11px] text-muted-foreground">
        <span className="uppercase tracking-wider">Legend:</span>
        {['on_time', 'late', 'on_shift', 'missed', 'unscheduled', 'pto', 'sick', 'off'].map((k) => (
          <span key={k} className="flex items-center gap-1">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${ATT_STATUS[k].dot}`} />
            {ATT_STATUS[k].label}
          </span>
        ))}
        <span className="ml-auto opacity-70">Late threshold: {LATE_THRESHOLD_MIN} min</span>
      </div>

      <CardContent className="space-y-3">
        {viewMode === 'day' && (
          <AttendanceDayView
            day={day}
            todayKey={todayKey}
            employees={employees}
            shiftByKey={shiftByKey}
            attByKey={attByKey}
            loading={loading}
          />
        )}
        {viewMode === 'month' && (
          <AttendanceMonthView
            employee={staff.find((s) => s.id === employeeId)}
            monthDays={monthDays}
            monthAnchor={monthAnchor}
            todayKey={todayKey}
            employeeId={employeeId}
            shiftByKey={shiftByKey}
            attByKey={attByKey}
            loading={loading}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceDayView({ day, todayKey, employees, shiftByKey, attByKey, loading }) {
  const friendly = new Date(day + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const tplFromShift = (sh) => tplFor(sh);

  // Summary KPIs across all employees
  const summary = React.useMemo(() => {
    let onTime = 0, late = 0, missed = 0, unsched = 0, onShift = 0, pto = 0, sick = 0, off = 0, scheduled = 0;
    for (const emp of employees) {
      const sh = shiftByKey[`${emp.id}|${day}`];
      const att = attByKey[`${emp.id}|${day}`];
      const st = deriveStatus({ shift: sh, att, dateKey: day, todayKey });
      if (st.key === 'on_time') onTime++;
      else if (st.key === 'late') late++;
      else if (st.key === 'missed') missed++;
      else if (st.key === 'unscheduled') unsched++;
      else if (st.key === 'on_shift') onShift++;
      else if (st.key === 'pto') pto++;
      else if (st.key === 'sick') sick++;
      else if (st.key === 'scheduled') scheduled++;
      else off++;
    }
    return { onTime, late, missed, unsched, onShift, pto, sick, off, scheduled };
  }, [employees, shiftByKey, attByKey, day, todayKey]);

  return (
    <div className="space-y-3">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        <KpiPill label="On time"     value={summary.onTime}   tone="emerald" />
        <KpiPill label="Late"        value={summary.late}     tone="amber" />
        <KpiPill label="On shift"    value={summary.onShift}  tone="blue" />
        <KpiPill label="Missed"      value={summary.missed}   tone="rose" />
        <KpiPill label="Unscheduled" value={summary.unsched}  tone="sky" />
        <KpiPill label="PTO"         value={summary.pto}      tone="violet" />
        <KpiPill label="Sick"        value={summary.sick}     tone="amber" />
        <KpiPill label="Off"         value={summary.off + summary.scheduled} tone="stone" />
      </div>

      <p className="text-xs text-muted-foreground">{loading ? 'Loading…' : friendly}</p>

      <div className="overflow-auto rounded border border-border">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-2">Employee</th>
              <th className="p-2">Role</th>
              <th className="p-2">Scheduled</th>
              <th className="p-2">Clock in</th>
              <th className="p-2">Clock out</th>
              <th className="p-2 text-right">Hours</th>
              <th className="p-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {!employees.length && (
              <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">No matching staff.</td></tr>
            )}
            {employees.map((emp) => {
              const sh = shiftByKey[`${emp.id}|${day}`];
              const att = attByKey[`${emp.id}|${day}`];
              const st = deriveStatus({ shift: sh, att, dateKey: day, todayKey });
              const tpl = sh ? tplFromShift(sh) : null;
              return (
                <tr key={emp.id} data-testid="att-row" className="border-t border-border">
                  <td className="p-2 font-medium">{emp.full_name}</td>
                  <td className="p-2 capitalize text-muted-foreground">{emp.role}</td>
                  <td className="p-2">
                    {sh ? (
                      <Badge className={`border ${tpl.cell}`}>{tpl.label}{sh.status === 'pto' || sh.status === 'sick' ? '' : ` ${sh.start_time}–${sh.end_time}`}</Badge>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="p-2 text-xs">
                    {st.actualIn ? <span className="tabular">{st.actualIn}</span> : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-2 text-xs">
                    {st.actualOut ? <span className="tabular">{st.actualOut}</span> : (att && !att.clock_out ? <Badge className={`border ${ATT_STATUS.on_shift.cell}`}><Clock className="mr-1 h-3 w-3" /> Active</Badge> : <span className="text-muted-foreground">—</span>)}
                  </td>
                  <td className="p-2 text-right tabular">{st.mins != null ? `${(st.mins / 60).toFixed(1)} h` : '—'}</td>
                  <td className="p-2 text-right">
                    <Badge className={`border ${ATT_STATUS[st.key].cell}`}>
                      <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ATT_STATUS[st.key].dot}`} />
                      {ATT_STATUS[st.key].label}{st.key === 'late' && st.lateBy ? ` · ${st.lateBy}m` : ''}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttendanceMonthView({ employee, monthDays, monthAnchor, todayKey, employeeId, shiftByKey, attByKey, loading }) {
  // Per-day status for this employee
  const cellData = React.useMemo(() => {
    if (!employeeId) return {};
    const m = {};
    for (const d of monthDays) {
      const key = ymd(d);
      const sh = shiftByKey[`${employeeId}|${key}`];
      const att = attByKey[`${employeeId}|${key}`];
      const st = deriveStatus({ shift: sh, att, dateKey: key, todayKey });
      m[key] = { status: st, shift: sh, att };
    }
    return m;
  }, [monthDays, employeeId, shiftByKey, attByKey, todayKey]);

  // Summary KPIs for the month (only days inside the actual month)
  const summary = React.useMemo(() => {
    let daysWorked = 0;
    let totalHours = 0;
    let onTime = 0, late = 0, missed = 0;
    let scheduled = 0;
    for (const d of monthDays) {
      if (d.getMonth() !== monthAnchor.getMonth()) continue;
      const k = ymd(d);
      const c = cellData[k];
      if (!c) continue;
      const st = c.status.key;
      if (c.status.mins != null) { totalHours += c.status.mins / 60; daysWorked++; }
      if (st === 'on_time') onTime++;
      if (st === 'late') late++;
      if (st === 'missed') missed++;
      if (c.shift && (c.shift.status === 'scheduled' || c.shift.status === 'completed')) scheduled++;
    }
    return { daysWorked, totalHours, onTime, late, missed, scheduled, onTimePct: scheduled ? Math.round((onTime / scheduled) * 100) : null };
  }, [cellData, monthDays, monthAnchor]);

  if (!employee) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Pick a staff member to view their month.</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      {/* Calendar */}
      <div className="overflow-hidden rounded border border-border">
        <div className="grid grid-cols-7 bg-muted/40 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="border-b border-border p-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {monthDays.map((d) => {
            const k = ymd(d);
            const c = cellData[k];
            const inMonth = d.getMonth() === monthAnchor.getMonth();
            const isToday = k === todayKey;
            const cfg = c?.status ? ATT_STATUS[c.status.key] : null;
            return (
              <div
                key={k}
                data-testid="att-month-cell"
                className={`min-h-[96px] border-b border-l border-border p-2 text-left transition ${inMonth ? 'bg-card' : 'bg-muted/10'}`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className={`font-display text-sm ${inMonth ? 'text-foreground' : 'text-muted-foreground/60'} ${isToday ? '!text-primary font-semibold' : ''}`}>{d.getDate()}</span>
                  {cfg && c.status.key !== 'off' && (
                    <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                  )}
                </div>
                {cfg && c.status.key !== 'off' && (
                  <div className="space-y-1">
                    <Badge className={`block max-w-full truncate border text-[10px] ${cfg.cell}`}>
                      {cfg.label}{c.status.key === 'late' && c.status.lateBy ? ` · ${c.status.lateBy}m` : ''}
                    </Badge>
                    {c.status.actualIn && (
                      <p className="text-[10px] leading-tight text-muted-foreground tabular">
                        {c.status.actualIn}{c.status.actualOut ? `–${c.status.actualOut}` : '–…'}
                      </p>
                    )}
                    {c.status.mins != null && (
                      <p className="text-[10px] leading-tight text-muted-foreground tabular">{(c.status.mins / 60).toFixed(1)} h</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary card */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{employee.full_name}</CardTitle>
            <p className="text-xs capitalize text-muted-foreground">{employee.role}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Days worked" value={summary.daysWorked} tone="emerald" />
              <Stat label="Total hours" value={`${summary.totalHours.toFixed(1)}h`} tone="blue" />
              <Stat label="On time" value={summary.onTime} tone="emerald" />
              <Stat label="Late" value={summary.late} tone="amber" />
              <Stat label="Missed" value={summary.missed} tone="rose" />
              <Stat label="Scheduled" value={summary.scheduled} tone="stone" />
            </div>
            <div className="rounded border border-border bg-muted/30 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">On-time rate</p>
              <p className="mt-1 font-display text-2xl font-semibold text-primary tabular">
                {summary.onTimePct == null ? '—' : `${summary.onTimePct}%`}
              </p>
              <p className="text-[10px] text-muted-foreground">of scheduled shifts</p>
            </div>
            {loading && <p className="text-center text-xs text-muted-foreground">Loading…</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiPill({ label, value, tone = 'stone' }) {
  const map = {
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber:   'bg-amber-50 text-amber-900 border-amber-200',
    blue:    'bg-blue-50 text-blue-900 border-blue-200',
    rose:    'bg-rose-50 text-rose-900 border-rose-200',
    sky:     'bg-sky-50 text-sky-900 border-sky-200',
    violet:  'bg-violet-50 text-violet-900 border-violet-200',
    stone:   'bg-stone-50 text-stone-900 border-stone-200',
  };
  return (
    <div data-testid={`att-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`} className={`flex items-center justify-between rounded border px-3 py-2 ${map[tone] || map.stone}`}>
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
      <span className="font-display text-base font-semibold tabular">{value}</span>
    </div>
  );
}

function Stat({ label, value, tone = 'stone' }) {
  const map = {
    emerald: 'text-emerald-700',
    amber:   'text-amber-700',
    blue:    'text-blue-700',
    rose:    'text-rose-700',
    stone:   'text-stone-700',
  };
  return (
    <div className="rounded border border-border bg-card p-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 font-display text-lg font-semibold tabular ${map[tone] || map.stone}`}>{value}</p>
    </div>
  );
}

